import { Typography } from '@navi/web-ui/lib/primitives';
import {
  ReallocationCaseType,
  ReallocationReasons,
  ReallocationStatus,
  ReallocationType,
  ReallocationTypeOptions
} from './reallocationInterfaces';
import { ICellRendererParams } from 'ag-grid-community';
import styles from '../components/index.module.scss';
import { getFormattedDateTime, utcToIST } from 'src/utils/DateHelper';
import { ArrowDownIcon, ArrowUpIcon } from '@navi/web-ui/lib/icons';
import cx from 'classnames';
import { IOption } from 'src/components/AutocompleteDropdown/interfaces';

export const REALLOCATION_REASONS = [
  {
    value: ReallocationReasons.AGENT_RESIGN,
    label: 'Resignation',
    source: 'User will be deactivated'
  },
  {
    value: ReallocationReasons.AGENT_ABSCONDS,
    label: 'Absconded',
    source: 'User will be deactivated'
  },
  {
    value: ReallocationReasons.AGENT_LONG_LEAVE,
    label: 'Long leave'
  },
  {
    value: ReallocationReasons.AGENT_TERMINATED,
    label: 'Terminated',
    source: 'User will be deactivated'
  },
  {
    value: ReallocationReasons.AGENT_PIP,
    label: 'Low performing or serving PIP'
  },
  {
    value: ReallocationReasons.OTHERS,
    label: 'Others'
  }
];

export const CASE_TYPE = [
  {
    value: ReallocationCaseType.ALL,
    label: 'All'
  },
  {
    value: ReallocationCaseType.DPD_1_TO_30,
    label: 'DPD 1-30 cases'
  },
  {
    value: ReallocationCaseType.BLACKOUT,
    label: '30+ Blackout period cases'
  }
];

export const REALLOCATION_HISTORY_COLUMNS = [
  {
    headerName: 'Type',
    width: 200,
    cellRenderer: (params: ICellRendererParams) => {
      const fromAgentName = params.data?.jobData?.reallocationRequest?.sourceAgentName;
      const reallocationType =
        ReallocationTypeOptions[
          params.data?.requestSubType as keyof typeof ReallocationTypeOptions
        ] ?? params.data?.requestSubType;
      return (
        <div>
          <Typography variant="h4">{reallocationType}</Typography>
          <Typography variant="p3" className={styles.greyText}>
            from {fromAgentName}
          </Typography>
        </div>
      );
    }
  },
  {
    headerName: 'LANs uploaded',
    width: 150,
    cellRenderer: (params: ICellRendererParams) => {
      const { uploadStatus, successCount = 0, failureCount = 0 } = params.data;
      if (uploadStatus === ReallocationStatus.COMPLETED) {
        return (
          <Typography variant="h4" className={styles.greenText}>
            {successCount}
            <Typography variant="p3" className={styles.greyText} as="span">
              {' '}
              / {successCount + failureCount}
            </Typography>
          </Typography>
        );
      }
      return (
        <Typography variant="h4" className={styles.greenText}>
          In progress..
        </Typography>
      );
    }
  },
  {
    headerName: 'Failed',
    width: 100,
    cellRenderer: (params: ICellRendererParams) => {
      const { failureCount } = params.data;
      if (failureCount > 0) {
        return (
          <Typography variant="h4" className={styles.redText}>
            {failureCount}
          </Typography>
        );
      }
      return '---';
    }
  },
  {
    headerName: 'Uploaded on and completed at',
    width: 160,
    cellRenderer: (params: ICellRendererParams) => {
      const { startTime, endTime } = params.data;
      const startTimeIST = utcToIST(startTime);
      // TODO: Update endTime after 31 days
      const endTimeIST = utcToIST(startTime);
      return (
        <div>
          <Typography variant="p3">
            {startTime ? getFormattedDateTime(new Date(startTimeIST)) : '---'}
          </Typography>
          <Typography variant="p3">
            {endTime ? getFormattedDateTime(new Date(endTimeIST)) : '---'}
          </Typography>
        </div>
      );
    }
  },
  {
    headerName: 'Actions',
    width: 130,
    cellRenderer: (params: ICellRendererParams) => {
      const { toggleState, uploadStatus } = params.data;
      return (
        <Typography
          variant="h4"
          className={cx(styles.actionText, {
            [styles.actionTextDisabled]: uploadStatus !== ReallocationStatus.COMPLETED
          })}
        >
          {toggleState ? 'Hide details' : 'View details'}
          {toggleState ? (
            <ArrowUpIcon color={'var(--blue-base)'} />
          ) : (
            <ArrowDownIcon color={'var(--blue-base)'} />
          )}
        </Typography>
      );
    }
  }
];

export const MULTISELECT_CHIP_DISPLAY_COUNT = 4;

export const defaultPageOptionsAgents = {
  pageSize: 30,
  pageNo: 0
};

export const defaultPageOptionsHistory = {
  pageSize: 10,
  pageNo: 0
};

export const isReallocationFormValid = (
  fromAgentReferenceId: string,
  toAgentReferenceIds: string[],
  reallocationType: string,
  reasonType: string,
  reason: string,
  maxAgentCount: number,
  selectedAgents: IOption[],
  subReallocationType: string
) => {
  const isOthersReason = reasonType === ReallocationReasons.OTHERS;

  if (!fromAgentReferenceId) {
    return false;
  }

  if (!subReallocationType) {
    return false;
  }

  if (
    !toAgentReferenceIds?.length &&
    reallocationType !== ReallocationType.TO_SAME_CYCLE &&
    subReallocationType !== ReallocationCaseType.BLACKOUT
  ) {
    return false;
  }

  if (
    reallocationType !== ReallocationType.TO_SAME_CYCLE &&
    selectedAgents?.length > maxAgentCount &&
    subReallocationType !== ReallocationCaseType.BLACKOUT
  ) {
    return false;
  }

  if (!reasonType) {
    return false;
  }

  if (isOthersReason && !reason) {
    return false;
  }

  return true;
};
